/**
 * @file   src\Pages\MonthlyMembershipPurchaseReport.tsx
 * @brief  Monthly Membership Purchase Report page for each carwash.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  Container,
  Table,
  Breadcrumb,
  useNavigate,
  useEffect,
  useOutletContext,
  useState,
  Button,
  useParams,
  Pagination,
  React,
} from "../utils/thirdpartyComponents";
import { userType, serviceType } from "../utils/enums";
import { IMultiLocationUsage } from "../interfaces/generalInterface";
import { pageURLs } from "../utils/pageUrls";
import { AppContextType } from "../interfaces/generalInterface";
import axios from "../config/axiosConfig";
import { CompanyApi, RevenueApi } from "../utils/apiUrls";
import SmallLoader from "../components/SmallLoader";
import CWSelect from "../components/CWSelect";
import { MessageToaster } from "../utils/toastUtil";
import DatePicker from 'react-datepicker';
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { MONTHS_OPTIONS } from "../utils/constants";

const MonthlyMembershipPurchaseReport = (props: any) => {
  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  const carwash_owner =
    props.userType === userType["CARWASH-OWNER"] ? true : false;

  const navigate = useNavigate();

  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [washRecords, setWashRecords] = useState<IMultiLocationUsage[]>([]);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  
  const [filters, setFilters] = useState({
    month: "",
    year: "",
    bayType: "",
    fromDate: "",
    toDate: "",
  });

  const today = new Date();
  const timezoneOffset = new Date().getTimezoneOffset();
  const toaster = new MessageToaster();

  // State to company details
  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const pageSize = 10;

  // To display heading
  useEffect(() => {
    getCompany();
    getCompanyAddress();
  }, []);

  useEffect(() => {
    handleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    if (filters.month && filters.year) {
      getMembershipPurchaseReport(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Get min and max dates based on selected month and year
  const getDateRange = () => {
    if (!filters.month || !filters.year) {
      return { minDate: null, maxDate: null };
    }
    const year = parseInt(filters.year);
    const month = parseInt(filters.month) - 1; // JavaScript months are 0-indexed
    const minDate = new Date(year, month, 1);
    const maxDate = new Date(year, month + 1, 0); // Last day of the month
    return { minDate, maxDate };
  };

  // Handle DatePicker changes
  const handleFromDateChange = (date: Date | null) => {
    setFromDate(date);
    if (date) {
      setFilters((prev) => ({
        ...prev,
        fromDate: moment(date).format('YYYY-MM-DD'),
      }));
    } else {
      setFilters((prev) => ({ ...prev, fromDate: "" }));
    }
  };

  const handleToDateChange = (date: Date | null) => {
    setToDate(date);
    if (date) {
      setFilters((prev) => ({
        ...prev,
        toDate: moment(date).format('YYYY-MM-DD'),
      }));
    } else {
      setFilters((prev) => ({ ...prev, toDate: "" }));
    }
  };

  const getCompanyAddress = async () => {
    try {
      const id = params.id ? parseInt(params.id) : 0;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id);
      if (data?.error === 0) {
        setCompany(data.data);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  // Update the data
  const handleUpdate = () => {
    updateData({
      heading: "Monthly Membership Purchases",
      url: carwash_owner
        ? ""
        : `/superadmin/${pageURLs.carwash_locations}/${params.id}`,
      showBackButton:
        props.userType === userType["CARWASH-OWNER"] ? false : true,
      showMainMenu: true,
      activeLink:
        props.userType === userType["CARWASH-OWNER"]
          ? "reports"
          : "manage-car-wash",
    });
  };

  const handleFilterChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const getBayTypeLabel = (bayType: number) => {
    const bayTypeMap: { [key: number]: string } = {
      [serviceType.Inbay]: "In Bay/Tunnel",
      [serviceType.SelfServe]: "Self Serve",
      [serviceType.DogWash]: "Dog Wash",
      [serviceType.Vacuum]: "Vacuum",
    };
    return bayTypeMap[bayType] || "Unknown";
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return moment.utc(dateString).format("MM/DD/YYYY");
  };

  // Common function to fetch report data from API
  const fetchReportData = async (pageNo: number, pageSizeParam: number) => {
    if (filters.month === '' || filters.year === '') {
      return null;
    }

    const queryParams = new URLSearchParams();

    // pagination
    const offset = timezoneOffset;
    queryParams.append("offset", offset.toString());
    queryParams.append("pageNo", pageNo.toString());
    queryParams.append("pageSize", pageSizeParam.toString());

    // required carwash
    if (params.id) {
      queryParams.append("carWashId", params.id.toString());
    }

    // filters
    if (filters.bayType && filters.bayType !== "all") queryParams.append("bayType", filters.bayType);
    if (filters.month) queryParams.append("month", filters.month);
    if (filters.year) queryParams.append("year", filters.year);

    // custom date range if selected
    if (filters.fromDate) {
      queryParams.append("fromDate", filters.fromDate);
    }
    if (filters.toDate) {
      queryParams.append("toDate", filters.toDate);
    }

    const apiUrl = RevenueApi.CARWASH_OWNER_MEMBERSHIP_PURCHASE_REPORT;
    
    const url = `${apiUrl}?${queryParams.toString()}`;
    const { data } = await axios.get(url);

    if (data?.error === 0 && Array.isArray(data?.data?.list)) {
      return {
        list: data.data.list,
        totalCount: data.data.totalCount,
        limit: data.data.limit
      };
    }
    
    return null;
  };

  const getMembershipPurchaseReport = async (pageNo = 1) => {
    try {
      setLoading(true);
      const result = await fetchReportData(pageNo, pageSize);

      if (result) {
        if (result.list?.length > 0) {
          setWashRecords(result.list);
          setTotalPages(Math.ceil(result.totalCount / result.limit) || 1);
        } else {
          setWashRecords([]);
        }
      } else {
        setWashRecords([]);
      }
    } catch (error: any) {
      console.error("Error fetching monthly membership purchase report:", error.message);
      toaster.toastError(error.response?.data?.message || error.message || "Failed to fetch report");
      setWashRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadReportAsExcel = async () => {
    if (!filters.month || !filters.year) {
      toaster.toastError("Please select month and year before downloading report.");
      return;
    }

    try {
      setLoading(true);
      const result = await fetchReportData(1, 0); // pageNo = 1, pageSize = 0 to get all records

      if (result && result.list?.length > 0) {
        const worksheetData = result.list.map((item: any) => ({
          Date: formatDate(item?.TransactionDate ?? ""),
          "Location Name": item?.LocationName ?? "",
          "Bay Type": getBayTypeLabel(item?.BayType ?? 0),
          Amount: item?.Amount ?? item?.Revenue ?? 0,
        }));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "MonthlyMembershipPurchase");

        const workbookArray = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([workbookArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const fileName = `MonthlyMembershipPurchase_${filters.year || "AllYears"}_${filters.month || "AllMonths"}.xlsx`;
        saveAs(blob, fileName);
      } else {
        toaster.toastError("No records to download.");
      }
    } catch (error: any) {
      console.error("Error downloading monthly membership purchase report:", error.message);
      toaster.toastError(error.response?.data?.message || error.message || "Failed to download report");
    } finally {
      setLoading(false);
    }
  };

  // Navigate according to url
  const itemClick = (url: string) => {
    navigate(url);
  };

  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalPageNumbersToShow / 2)
    );
    let endPage = startPage + totalPageNumbersToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const BayTypeOptions = [
    { label: "All", value: "all" },
    { label: "In Bay/Tunnel", value: serviceType.Inbay.toString() },
    { label: "Self Serve", value: serviceType.SelfServe.toString() },
    { label: "Dog Wash", value: serviceType.DogWash.toString() },
    { label: "Vacuum", value: serviceType.Vacuum.toString() },
  ];

  const yearOptions = Array.from(
    { length: new Date().getFullYear() - 2024 + 1 },
    (_, i) => {
      const year = 2024 + i;
      return { label: year.toString(), value: year.toString() };
    }
  );

  // Function to fetch company information
  const getCompany = async () => {
    try {
      const param1 = `${params.id}`;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS)
        : await axios.get(CompanyApi.GET_COMPANY + param1);
    } catch (error: any) {
      console.error("Error fetching company information:", error.message);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  return (
    <div className="manage-carwash-container order-list-container multi-location-wrap">
      <Container>
        <Breadcrumb>
          {carwash_owner ? (
            <>
              <Breadcrumb.Item
                onClick={() => itemClick(`/${pageURLs.dashboard}`)}
              >
                Dashboard
              </Breadcrumb.Item>
            </>
          ) : (
            <>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(`/superadmin/${pageURLs.manage_carwash}`);
                }}
              >
                Manage Car Washes
              </Breadcrumb.Item>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(
                    `/superadmin/${pageURLs.carwash_locations}/${params.id}`
                  );
                }}
              >
                {company.companyName}
              </Breadcrumb.Item>
            </>
          )}
          <Breadcrumb.Item active>Monthly Membership Purchases</Breadcrumb.Item>
        </Breadcrumb>

        <div className="manage-header-block d-flex justify-content-between align-items-center">
          <div className="search-wrap multi-location-action w-100">
            <div className="result-group d-flex align-items-center">
        
              <CWSelect
                id="selectyear"
                name="selectyear"
                options={yearOptions}
                label="Select Year"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) => {
                  handleFilterChange("year", option[0]?.value?.toString() || "");
                  // Reset dates when year changes
                  setFromDate(null);
                  setToDate(null);
                  setFilters((prev) => ({ ...prev, fromDate: "", toDate: "" }));
                }}
              />
              <CWSelect
                id="selectmonth"
                name="selectmonth"
                options={MONTHS_OPTIONS}
                label="Select Month"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) => {
                  handleFilterChange("month", option[0]?.value?.toString() || "");
                  // Reset dates when month changes
                  setFromDate(null);
                  setToDate(null);
                  setFilters((prev) => ({ ...prev, fromDate: "", toDate: "" }));
                }}
              />
              <div className="d-flex flex-column">
                <label className="form-label">
                  Date Range
                </label>
                <div className="d-flex align-items-center">
                  {(() => {
                    const { minDate, maxDate } = getDateRange();
                    const isDatePickerDisabled = !filters.month || !filters.year;
                    return (
                      <>
                        <DatePicker
                          showIcon
                          selected={fromDate}
                          onChange={handleFromDateChange}
                          placeholderText="From Date"
                          disabled={isDatePickerDisabled}
                          minDate={minDate || undefined}
                          maxDate={maxDate || undefined}
                          dateFormat="MM/dd/yyyy"
                        />
                        <div className="ms-3">
                          <DatePicker
                            showIcon
                            selected={toDate}
                            onChange={handleToDateChange}
                            placeholderText="To Date"
                            disabled={isDatePickerDisabled}
                            minDate={fromDate || minDate || undefined}
                            maxDate={maxDate || undefined}
                            dateFormat="MM/dd/yyyy"
                          />
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              <CWSelect
                id="baytype"
                name="baytype"
                options={BayTypeOptions}
                label="Bay Type"
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) =>
                  handleFilterChange("bayType", option[0]?.value?.toString() || "")
                }
              />
        <div className="button-group d-flex">
          <Button
          className="primary-btn"
          variant="primary"
          title="Run Report"
          onClick={() => {
            // Only fetch when all filters are selected
            if (filters.month && filters.year) {
              setCurrentPage(1); // Reset to first page
              getMembershipPurchaseReport(1);
            } else {
              toaster.toastError("Please select month and year before running report.");
            }
          }}
          >
            Run Report
          </Button>
          <Button
            className="secondary-btn ms-3"
            variant="outline-primary"
            title="Download Excel"
            onClick={downloadReportAsExcel}
          >
            Download Excel
          </Button>
        </div>
 
            </div>
          </div>
        </div>
        <div className="cw-table-wrap report-table">
          <Table responsive className="m-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Location Name</th>
                <th>Bay Type</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {washRecords.length > 0 ? (
                washRecords.map((item: any, index) => (
                  <tr key={index}>
                    <td>{formatDate(item?.TransactionDate ?? "")}</td>
                    <td>{item?.LocationName ?? ""}</td>
                    <td>{getBayTypeLabel(item?.BayType ?? 0)}</td>
                    <td>${((item?.Amount ?? item?.Revenue ?? 0)).toFixed(2)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center">
                    {loading ? "Loading..." : "No records found"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {washRecords && washRecords?.length ? (
            <div className="mt-3 d-flex justify-content-end">
              <Pagination>
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {getVisiblePageNumbers().map((pageNumber) => (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          ) : null}
        </div>
      </Container>

      {loading && <SmallLoader />}
    </div>
  );
};

export default MonthlyMembershipPurchaseReport;

